import React, { useState, useRef, useCallback } from 'react';
import api from '../services/api';
import FolderPickerModal from './FolderPickerModal';

const ACCEPTED_MIME = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
];

const ACCEPTED_EXT_LABEL = 'PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX, TXT';

const FILE_ICON = {
  'application/pdf': '📄',
  'application/msword': '📝',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '📝',
  'application/vnd.ms-powerpoint': '📊',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': '📊',
  'application/vnd.ms-excel': '📋',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '📋',
  'text/plain': '📃',
};

const stripExt = (name) => name.replace(/\.[^/.]+$/, '').trim() || name;

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * BulkUploadModal
 *
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - folders: array of folder objects
 *  - initialFolderId: string (optional pre-selected folder)
 *  - onSuccess: (createdDocs) => void — called after all uploads finish
 */
const BulkUploadModal = ({ isOpen, onClose, folders = [], initialFolderId = '', onSuccess }) => {
  const [files, setFiles] = useState([]); // { file, title, status: 'pending'|'uploading'|'done'|'error', error }
  const [folder, setFolder] = useState(initialFolderId);
  const [docType, setDocType] = useState('document');
  const [visibility, setVisibility] = useState('public');
  const [released, setReleased] = useState(false);
  const [releaseDate, setReleaseDate] = useState(new Date().toISOString().split('T')[0]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showFolderPicker, setShowFolderPicker] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null); // { created, errors }
  const [globalError, setGlobalError] = useState('');
  const fileInputRef = useRef(null);

  // Reset when modal is opened
  React.useEffect(() => {
    if (isOpen) {
      setFiles([]);
      setFolder(initialFolderId);
      setDocType('document');
      setVisibility('public');
      setReleased(false);
      setReleaseDate(new Date().toISOString().split('T')[0]);
      setUploadResult(null);
      setGlobalError('');
    }
  }, [isOpen, initialFolderId]);

  const addFiles = useCallback((incoming) => {
    const valid = Array.from(incoming).filter(f => ACCEPTED_MIME.includes(f.type));
    const invalid = Array.from(incoming).filter(f => !ACCEPTED_MIME.includes(f.type));

    if (invalid.length > 0) {
      setGlobalError(`${invalid.length} file(s) skipped (unsupported type). Accepted: ${ACCEPTED_EXT_LABEL}`);
    } else {
      setGlobalError('');
    }

    setFiles(prev => {
      // Deduplicate by name + size
      const existing = new Set(prev.map(e => `${e.file.name}::${e.file.size}`));
      const newEntries = valid
        .filter(f => !existing.has(`${f.name}::${f.size}`))
        .map(f => ({ file: f, title: stripExt(f.name), status: 'pending', error: null }));
      return [...prev, ...newEntries];
    });
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const handleFileInput = (e) => {
    addFiles(e.target.files);
    e.target.value = '';
  };

  const removeFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const updateTitle = (idx, val) => {
    setFiles(prev => prev.map((f, i) => i === idx ? { ...f, title: val } : f));
  };

  const folderName = folder
    ? (folders.find(f => f._id === folder)?.title || 'Selected Folder')
    : 'Root Folder';

  const handleUpload = async () => {
    const pendingIndexes = files
      .map((f, i) => (f.status === 'pending' || f.status === 'error' ? i : -1))
      .filter(i => i !== -1);

    if (pendingIndexes.length === 0) return;

    setUploading(true);
    setGlobalError('');

    let currentResult = uploadResult ? { ...uploadResult } : { created: [], errors: [] };

    // Helper to resolve the full path name of the folder hierarchy
    const getFolderPath = (folderId, allFolders) => {
      if (!folderId) return '';
      const current = allFolders.find(f => f._id === folderId);
      if (!current) return '';
      const parentPath = getFolderPath(current.parent, allFolders);
      return parentPath ? `${parentPath}/${current.title}` : current.title;
    };
    const resolvedFolderPath = getFolderPath(folder, folders);

    for (let i of pendingIndexes) {
      setFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'uploading', error: null } : f));

      currentResult.errors = currentResult.errors.filter(e => e.file !== files[i].title && e.file !== files[i].file.name);
      setUploadResult({ ...currentResult });

      const entry = files[i];
      const formData = new FormData();
      formData.append('type', docType);
      formData.append('folder', folder || '');
      formData.append('folderPath', resolvedFolderPath);
      formData.append('visibility', visibility);
      formData.append('released', String(released));
      formData.append('releaseDate', releaseDate);

      const ext = entry.file.name.includes('.') ? '.' + entry.file.name.split('.').pop() : '';
      const renamedFile = new File([entry.file], entry.title + ext, { type: entry.file.type });
      formData.append('files', renamedFile);

      try {
        const res = await api.post('/upload/bulk', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        const { created = [], errors = [] } = res.data;

        if (errors && errors.length > 0) {
          const errMsg = typeof errors[0].error === 'string' ? errors[0].error : JSON.stringify(errors[0].error);
          setFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'error', error: errMsg } : f));
          currentResult.errors.push({ file: entry.title, error: errMsg });
        } else {
          setFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'done', error: null } : f));
          if (created.length > 0) {
             currentResult.created.push(created[0]);
          }
        }
      } catch (err) {
        let msg = 'Upload failed. Please try again.';
        if (err.response?.data?.error) {
          msg = typeof err.response.data.error === 'string' 
            ? err.response.data.error 
            : JSON.stringify(err.response.data.error);
        } else if (err.message) {
          msg = err.message;
        }
        setFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'error', error: msg } : f));
        currentResult.errors.push({ file: entry.title, error: msg });
      }
      
      setUploadResult({ ...currentResult });
    }

    setUploading(false);

    if (currentResult.errors.length === 0 && onSuccess && currentResult.created.length > 0) {
      onSuccess(currentResult.created);
    }
  };

  if (!isOpen) return null;

  const pendingCount = files.filter(f => f.status === 'pending').length;
  const doneCount = files.filter(f => f.status === 'done').length;
  const errorCount = files.filter(f => f.status === 'error').length;
  const isFinished = uploadResult !== null && pendingCount === 0 && errorCount === 0;

  return (
    <div
      className="modal-overlay"
      onClick={uploading ? undefined : onClose}
      style={{ zIndex: 12000 }}
    >
      <div
        className="modal-card"
        onClick={e => e.stopPropagation()}
        style={{
          width: 'min(720px, 96vw)',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: 0,
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 24px',
          borderBottom: '1px solid var(--border-light)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'var(--bg-card)',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '38px', height: '38px',
              background: 'linear-gradient(135deg, var(--primary), var(--primary-light, #818cf8))',
              borderRadius: '10px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.1rem',
            }}>
              📦
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700 }}>Bulk Upload Documents</h2>
              <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                File name → document title &nbsp;·&nbsp; Up to 50 files, 50 MB each
              </p>
            </div>
          </div>
          {!uploading && (
            <button
              type="button"
              className="premium-icon-btn"
              onClick={onClose}
              style={{ width: '32px', height: '32px', fontSize: '1rem' }}
              title="Close"
            >
              <i className="fas fa-times" />
            </button>
          )}
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Settings row */}
          {!isFinished && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
              {/* Folder picker */}
              <div style={{ display: 'grid', gap: '6px' }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Target Folder
                </label>
                <button
                  type="button"
                  className="secondary"
                  onClick={() => setShowFolderPicker(true)}
                  disabled={uploading}
                  style={{ textAlign: 'left', padding: '8px 12px', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}
                >
                  <i className="fas fa-folder" style={{ color: 'var(--primary)', flexShrink: 0 }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{folderName}</span>
                </button>
              </div>

              {/* Type */}
              <div style={{ display: 'grid', gap: '6px' }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Document Type
                </label>
                <select
                  value={docType}
                  onChange={e => setDocType(e.target.value)}
                  disabled={uploading}
                  style={{ padding: '8px 12px', fontSize: '0.85rem' }}
                >
                  <option value="document">Document</option>
                  <option value="textbook">Textbook</option>
                  <option value="exam_paper">Exam Paper</option>
                </select>
              </div>

              {/* Visibility */}
              <div style={{ display: 'grid', gap: '6px' }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Visibility
                </label>
                <select
                  value={visibility}
                  onChange={e => setVisibility(e.target.value)}
                  disabled={uploading}
                  style={{ padding: '8px 12px', fontSize: '0.85rem' }}
                >
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
              </div>

              {/* Release date */}
              <div style={{ display: 'grid', gap: '6px' }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Release Date
                </label>
                <input
                  type="date"
                  value={releaseDate}
                  onChange={e => setReleaseDate(e.target.value)}
                  disabled={uploading}
                  style={{ padding: '8px 12px', fontSize: '0.85rem' }}
                />
              </div>
            </div>
          )}

          {/* Released toggle */}
          {!isFinished && (
            <label style={{
              display: 'flex', alignItems: 'center', gap: '10px', cursor: uploading ? 'default' : 'pointer',
              userSelect: 'none', background: 'var(--bg-main)', padding: '10px 14px',
              borderRadius: 'var(--radius-md)', border: '1px solid var(--border-light)', fontSize: '0.88rem'
            }}>
              <input
                type="checkbox"
                checked={released}
                onChange={e => setReleased(e.target.checked)}
                disabled={uploading}
                style={{ width: '16px', height: '16px', accentColor: 'var(--success)' }}
              />
              <span style={{ color: released ? 'var(--success)' : 'var(--text-muted)', fontWeight: 600 }}>
                {released ? '✓ Release immediately after upload' : 'Keep as Draft after upload'}
              </span>
            </label>
          )}

          {/* Drop zone */}
          {!isFinished && (
            <div
              onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
              onClick={() => !uploading && fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${isDragOver ? 'var(--primary)' : 'var(--border-light)'}`,
                borderRadius: 'var(--radius-md)',
                padding: '28px 20px',
                textAlign: 'center',
                cursor: uploading ? 'default' : 'pointer',
                background: isDragOver ? 'rgba(var(--primary-rgb, 99,102,241), 0.06)' : 'var(--bg-main)',
                transition: 'all 0.2s ease',
              }}
            >
              <div style={{ fontSize: '2rem', marginBottom: '8px' }}>
                {isDragOver ? '📂' : '⬆️'}
              </div>
              <p style={{ margin: 0, fontWeight: 600, color: 'var(--text-main)', fontSize: '0.95rem' }}>
                {isDragOver ? 'Drop files here' : 'Drag & drop files or click to browse'}
              </p>
              <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                {ACCEPTED_EXT_LABEL} · Up to 50 files
              </p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt"
                onChange={handleFileInput}
                style={{ display: 'none' }}
              />
            </div>
          )}

          {globalError && (
            <div className="error-message" style={{ padding: '10px 14px', fontSize: '0.85rem' }}>
              <i className="fas fa-exclamation-triangle" style={{ marginRight: '6px' }} />
              {globalError}
            </div>
          )}

          {/* File list */}
          {files.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Files ({files.length})
                </span>
                {!isFinished && !uploading && (
                  <button
                    type="button"
                    className="secondary small"
                    onClick={() => setFiles([])}
                    style={{ padding: '2px 8px', fontSize: '0.75rem' }}
                  >
                    Clear all
                  </button>
                )}
              </div>

              {files.map((entry, idx) => {
                const icon = FILE_ICON[entry.file.type] || '📄';
                const statusColor = {
                  pending: 'var(--text-muted)',
                  uploading: 'var(--primary)',
                  done: 'var(--success)',
                  error: 'var(--danger)',
                }[entry.status];

                return (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '8px 12px',
                      background: entry.status === 'done'
                        ? 'rgba(16,185,129,0.07)'
                        : entry.status === 'error'
                          ? 'rgba(239,68,68,0.07)'
                          : 'var(--bg-main)',
                      borderRadius: '8px',
                      border: `1px solid ${
                        entry.status === 'done' ? 'rgba(16,185,129,0.3)'
                        : entry.status === 'error' ? 'rgba(239,68,68,0.3)'
                        : 'var(--border-light)'
                      }`,
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{icon}</span>

                    {/* Editable title */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                      <input
                        type="text"
                        value={entry.title}
                        onChange={e => updateTitle(idx, e.target.value)}
                        disabled={uploading}
                        placeholder="Document title"
                        style={{
                          border: 'none',
                          background: 'transparent',
                          padding: '2px 0',
                          fontSize: '0.88rem',
                          fontWeight: 600,
                          color: 'var(--text-main)',
                          width: '100%',
                          outline: 'none',
                        }}
                        title="Edit document title"
                      />
                      <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                        {entry.file.name} &nbsp;·&nbsp; {formatBytes(entry.file.size)}
                      </span>
                    </div>

                    {/* Status indicator */}
                    <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {entry.status === 'uploading' && (
                        <i className="fas fa-spinner fa-spin" style={{ color: statusColor, fontSize: '0.85rem' }} />
                      )}
                      {entry.status === 'done' && (
                        <i className="fas fa-check-circle" style={{ color: statusColor, fontSize: '0.9rem' }} />
                      )}
                      {entry.status === 'error' && (
                        <i className="fas fa-times-circle" style={{ color: statusColor, fontSize: '0.9rem' }} title={entry.error || 'Failed'} />
                      )}
                    </div>

                    {/* Remove button (only when not uploading) */}
                    {!uploading && entry.status !== 'done' && (
                      <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                        {entry.status === 'error' && (
                          <button
                            type="button"
                            className="premium-icon-btn primary"
                            onClick={() => {
                              setFiles(prev => prev.map((f, i) => i === idx ? { ...f, status: 'pending', error: null } : f));
                              if (uploadResult) {
                                setUploadResult(prev => ({
                                  ...prev,
                                  errors: prev.errors.filter(e => e.file !== entry.title && e.file !== entry.file.name)
                                }));
                              }
                            }}
                            style={{ width: '26px', height: '26px', fontSize: '0.7rem', color: 'var(--primary)' }}
                            title="Retry file"
                          >
                            <i className="fas fa-redo" />
                          </button>
                        )}
                        <button
                          type="button"
                          className="premium-icon-btn danger"
                          onClick={() => removeFile(idx)}
                          style={{ width: '26px', height: '26px', fontSize: '0.7rem' }}
                          title="Remove file"
                        >
                          <i className="fas fa-times" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Upload result summary */}
          {uploadResult && (
            <div style={{
              padding: '14px 16px',
              borderRadius: 'var(--radius-md)',
              background: uploadResult.errors.length === 0
                ? 'rgba(16,185,129,0.1)'
                : 'rgba(245,158,11,0.1)',
              border: `1px solid ${uploadResult.errors.length === 0 ? 'var(--success)' : '#f59e0b'}`,
            }}>
              <p style={{ margin: 0, fontWeight: 700, color: uploadResult.errors.length === 0 ? 'var(--success)' : '#f59e0b' }}>
                {uploadResult.errors.length === 0
                  ? `✅ All ${uploadResult.created.length} document(s) uploaded successfully!`
                  : `⚠️ ${uploadResult.created.length} uploaded, ${uploadResult.errors.length} failed`}
              </p>
              {uploadResult.errors.length > 0 && (
                <ul style={{ margin: '8px 0 0', paddingLeft: '18px', fontSize: '0.82rem', color: 'var(--danger)' }}>
                  {uploadResult.errors.map((e, i) => (
                    <li key={i}>
                      <strong>{typeof e?.file === 'string' ? e.file : 'Unknown File'}</strong>: {typeof e?.error === 'string' ? e.error : JSON.stringify(e?.error || 'Error')}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 24px',
          borderTop: '1px solid var(--border-light)',
          background: 'var(--bg-card)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexShrink: 0,
        }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            {!isFinished && files.length > 0 && (
              <span>{files.length} file{files.length !== 1 ? 's' : ''} selected</span>
            )}
            {isFinished && (
              <span style={{ color: doneCount > 0 ? 'var(--success)' : 'var(--text-muted)' }}>
                {doneCount} created · {errorCount} failed
              </span>
            )}
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            {!isFinished && errorCount > 0 ? (
              <>
                <button
                  type="button"
                  className="secondary"
                  onClick={() => {
                    if (doneCount > 0 && onSuccess) onSuccess(uploadResult?.created || []);
                    else onClose();
                  }}
                  disabled={uploading}
                  style={{ padding: '8px 18px', fontSize: '0.88rem' }}
                >
                  {doneCount > 0 ? 'Close & Keep Uploaded' : 'Cancel'}
                </button>
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={uploading}
                  style={{ padding: '8px 22px', fontSize: '0.88rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  {uploading ? (
                    <><i className="fas fa-spinner fa-spin" /> Uploading…</>
                  ) : (
                    <><i className="fas fa-redo" /> Retry Failed</>
                  )}
                </button>
              </>
            ) : !isFinished ? (
              <>
                <button
                  type="button"
                  className="secondary"
                  onClick={onClose}
                  disabled={uploading}
                  style={{ padding: '8px 18px', fontSize: '0.88rem' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={uploading || files.length === 0}
                  style={{
                    padding: '8px 22px',
                    fontSize: '0.88rem',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    opacity: files.length === 0 ? 0.5 : 1,
                    cursor: files.length === 0 ? 'not-allowed' : 'pointer',
                  }}
                >
                  {uploading ? (
                    <><i className="fas fa-spinner fa-spin" /> Uploading…</>
                  ) : (
                    <><i className="fas fa-cloud-upload-alt" /> Upload {files.length > 0 ? `${files.length} File${files.length !== 1 ? 's' : ''}` : 'Files'}</>
                  )}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => {
                   if (onSuccess) onSuccess(uploadResult?.created || []);
                   else onClose();
                }}
                style={{ padding: '8px 22px', fontSize: '0.88rem', fontWeight: 700 }}
              >
                <i className="fas fa-check" style={{ marginRight: '6px' }} />
                Done
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Folder picker */}
      {showFolderPicker && (
        <FolderPickerModal
          title="Select Target Folder"
          folders={folders}
          allowRoot={true}
          initialFolderId={folder}
          onSelect={(id) => { setFolder(id); setShowFolderPicker(false); }}
          onClose={() => setShowFolderPicker(false)}
        />
      )}
    </div>
  );
};

export default BulkUploadModal;
